import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import '../../styles/UsageIndicator.css';

/**
 * UsageIndicator Component
 *
 * Displays current dog usage with a progress bar.
 * Shows warning states when near or at limit.
 */
const UsageIndicator = ({
    currentDogs,
    maxDogs,
    planName,
    showUpgradeLink,
    onUpgradeClick,
    compact
}) => {
    const { t } = useTranslation('subscription');
    const isUnlimited = maxDogs === null || maxDogs === undefined;
    const usagePercentage = isUnlimited ? 0 : Math.round((currentDogs / maxDogs) * 100);
    const remainingSlots = isUnlimited ? null : Math.max(0, maxDogs - currentDogs);

    const isNearLimit = !isUnlimited && usagePercentage >= 80 && usagePercentage < 100;
    const isAtLimit = !isUnlimited && currentDogs >= maxDogs;

    const getProgressBarClass = () => {
        if (isAtLimit) return 'progress-bar at-limit';
        if (isNearLimit) return 'progress-bar near-limit';
        return 'progress-bar';
    };

    const getStatusText = () => {
        if (isUnlimited) return t('usageIndicator.unlimited');
        if (isAtLimit) return t('usageIndicator.atLimit');
        if (isNearLimit) return t('usageIndicator.nearLimit');
        return t('usageIndicator.freeSlots', { count: remainingSlots });
    };

    if (compact) {
        return (
            <div className={`usage-indicator compact ${isAtLimit ? 'at-limit' : ''} ${isNearLimit ? 'near-limit' : ''}`}>
                <span className="usage-count">
                    {currentDogs}/{isUnlimited ? '∞' : maxDogs}
                </span>
                {isAtLimit && showUpgradeLink && (
                    <button className="upgrade-link-compact" onClick={onUpgradeClick}>
                        {t('usageIndicator.upgradeCompact')}
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className={`usage-indicator ${isAtLimit ? 'at-limit' : ''} ${isNearLimit ? 'near-limit' : ''}`}>
            <div className="usage-header">
                <span className="usage-label">{t('usageIndicator.dogsLabel')}</span>
                <span className="usage-count">
                    {currentDogs} / {isUnlimited ? '∞' : maxDogs}
                </span>
            </div>

            {!isUnlimited && (
                <div className="progress-container">
                    <div
                        className={getProgressBarClass()}
                        style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                    />
                </div>
            )}

            <div className="usage-footer">
                <span className={`status-text ${isAtLimit ? 'warning' : ''}`}>
                    {getStatusText()}
                </span>
                {planName && (
                    <span className="plan-name">{t('usageIndicator.planLabel')} {planName}</span>
                )}
            </div>

            {(isAtLimit || isNearLimit) && showUpgradeLink && (
                <button className="upgrade-link" onClick={onUpgradeClick}>
                    {t('usageIndicator.upgradePlan')}
                </button>
            )}
        </div>
    );
};

UsageIndicator.propTypes = {
    currentDogs: PropTypes.number.isRequired,
    maxDogs: PropTypes.number,
    planName: PropTypes.string,
    showUpgradeLink: PropTypes.bool,
    onUpgradeClick: PropTypes.func,
    compact: PropTypes.bool
};

UsageIndicator.defaultProps = {
    maxDogs: null,
    planName: null,
    showUpgradeLink: true,
    onUpgradeClick: () => {},
    compact: false
};

export default UsageIndicator;
